/**
 * OfflineBanner — R53.31
 *
 * Banner persistente in basso (sopra la BottomNav) che mostra:
 *  - "Sei offline" quando navigator.onLine === false
 *  - "N azioni in coda" quando la coda IndexedDB ha operazioni pendenti
 *  - Pulsante "Sincronizza ora" per drain manuale
 *  - "✓ Sincronizzate N azioni" come conferma visiva post-drain
 *
 * Montato globalmente in App.js così resta visibile anche se l'utente viene
 * rimandato a /login mentre è offline (la coda persiste in IndexedDB).
 */
import { useEffect, useState, useCallback } from "react";
import { CloudOff, RefreshCw, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { count, subscribe, drain, initAutoDrain, subscribeDrainResult } from "@/lib/offlineQueue";

// Pluralizzazione italiana minima per "azione"
function azioniLabel(n) {
  return n === 1 ? "1 azione" : `${n} azioni`;
}

export default function OfflineBanner() {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(0); // 0 idle, 1 syncing, 2 just-done
  const [justSynced, setJustSynced] = useState(0);

  const refresh = useCallback(async () => {
    setPending(await count());
  }, []);

  useEffect(() => {
    initAutoDrain(api);
    refresh();
    const unsub = subscribe(refresh);
    // Hook per ricevere il risultato del drain automatico (online event / polling)
    const unsubDrain = subscribeDrainResult((res) => {
      if (res && res.ok > 0) {
        setJustSynced(res.ok);
        setSyncing(2);
        setTimeout(() => setSyncing(0), 2200);
      }
    });
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    return () => {
      unsub();
      unsubDrain();
      window.removeEventListener("online", onUp);
      window.removeEventListener("offline", onDown);
    };
  }, [refresh]);

  const handleSync = async () => {
    if (syncing === 1) return;
    setSyncing(1);
    try {
      const res = await drain(api);
      setJustSynced(res?.ok || 0);
      if ((res?.ok || 0) > 0) {
        setSyncing(2);
        setTimeout(() => setSyncing(0), 2200);
      } else {
        setSyncing(0);
      }
    } catch {
      setSyncing(0);
    }
  };

  if (online && pending === 0 && syncing !== 2) return null;

  // Stato visivo: offline (rosso), pending (ambra), success (verde)
  let bg = "bg-amber-500";
  let icon = <CloudOff className="w-4 h-4" />;
  let label = `${azioniLabel(pending)} in coda · si sincronizzeranno appena torna la rete`;

  if (!online) {
    bg = "bg-red-600";
    icon = <CloudOff className="w-4 h-4" />;
    label = pending > 0
      ? `Sei offline · ${azioniLabel(pending)} in coda`
      : "Sei offline · le tue azioni verranno salvate localmente";
  } else if (syncing === 2) {
    bg = "bg-emerald-600";
    icon = <CheckCircle2 className="w-4 h-4" />;
    label = `Sincronizzate ${azioniLabel(justSynced)}`;
  } else if (pending > 0) {
    bg = "bg-amber-500";
    icon = <RefreshCw className={`w-4 h-4 ${syncing === 1 ? "animate-spin" : ""}`} />;
    label = `${azioniLabel(pending)} in coda · in attesa di sincronizzazione`;
  }

  return (
    <div
      data-testid="offline-banner"
      className={`fixed left-0 right-0 z-50 ${bg} text-white text-sm shadow-lg bottom-16 md:bottom-16`}
    >
      <div className="max-w-screen-lg mx-auto px-3 py-2 flex items-center gap-2">
        <span className="shrink-0">{icon}</span>
        <span className="flex-1 leading-tight" data-testid="offline-banner-label">{label}</span>
        {online && pending > 0 && syncing !== 2 && (
          <button
            data-testid="offline-banner-sync-btn"
            onClick={handleSync}
            disabled={syncing === 1}
            className="shrink-0 px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 active:bg-white/10 disabled:opacity-50 text-xs font-medium"
          >
            {syncing === 1 ? "Sincronizzo…" : "Sincronizza ora"}
          </button>
        )}
      </div>
    </div>
  );
}
